import { fixedUi } from '@/i18n/fixedUi';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { temuPlatformAPI } from '@/temu-platform/api/temuPlatformApi';
import { useTemuShop } from '@/temu-platform/context/TemuShopContext';
import {
  installTemuPlatformJsonConsole,
  logTemuSyncPlatformDumpForSyncKind,
} from '@/temu-platform/utils/temuPlatformJsonConsole';
import { isRequestAborted } from '@/utils/api';
import { showToast } from '@/utils/toast';

const STORAGE_KEY = 'ipe_temu_active_sync_tracks';
const TemuSyncJobContext = createContext(null);
const SHOP_SYNC_BUSY_MESSAGE = fixedUi("当前店铺正在同步中，请等待同步完成后可操作");

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/** 任务视图里是否带「部分失败」（后端 error / errorMessage / errors 非空）。 */
export function syncViewHasErrors(view) {
  if (!view || typeof view !== 'object') return false;
  if (String(view.errorMessage || view.error || '').trim()) return true;
  const errors = view.errors;
  if (errors && typeof errors === 'object' && !Array.isArray(errors)) {
    return Object.keys(errors).some((k) => String(errors[k] ?? '').trim());
  }
  if (Array.isArray(errors)) {
    return errors.some((item) => String(item ?? '').trim());
  }
  return false;
}

/**
 * 同步完成提示的类型：只有真正成功才是 success（绿色）。
 * 部分片段失败 → warning（橙色）；整体失败 → error（红色）。
 */
export function resolveSyncToastType(status, view) {
  if (status === 'failed' || status === 'error') return 'error';
  if (view && view.ok === false) return 'error';
  if (syncViewHasErrors(view)) return 'warning';
  return 'success';
}

/** @typedef {'product'|'publishRecord'|'certificate'|'certificateLibrary'|'compliance'|'complianceAudit'|'productTemplateBatchCopy'|'photoTemplateBatchCopy'|'certificateLibraryBatchCopy'|'photoAutoBind'|'certAutoBind'|'agencyAutoBind'|'finance'|'returnOrders'|'orders'|'environmental'|'shopProfile'|'freightTemplates'|'warehouses'|'models'|'sizeCharts'|'discuss'|'rrpAudit'|'rrpSkc'|'skcAgency'|'agency'|'stock'|'marketing'|'marketingProduct'|'custom'} SyncKind */

export const TEMU_SYNC_KINDS = {
  product: {
    get label() { return fixedUi("商品"); },
    mode: 'job',
    start: (shopId, payload) => temuPlatformAPI.startProductSyncJob(shopId, payload),
    get: (shopId, jobId) => temuPlatformAPI.getProductSyncJob(shopId, jobId),
    cancel: (shopId, jobId) => temuPlatformAPI.cancelProductSyncJob(shopId, jobId),
  },
  publishRecord: {
    get label() { return fixedUi('发布记录'); },
    mode: 'job',
    start: (shopId) => temuPlatformAPI.startPublishRecordSyncJob(shopId),
    get: (shopId, jobId) => temuPlatformAPI.getPublishRecordSyncJob(shopId, jobId),
    cancel: (shopId, jobId) => temuPlatformAPI.cancelPublishRecordSyncJob(shopId, jobId),
  },
  /** 产品证书资质：bg.arbok.open.product.cert.query → 落库待上传证书类型 */
  certificate: {
    get label() { return fixedUi('产品证书资质'); },
    mode: 'job',
    start: (shopId, payload) => temuPlatformAPI.startCertificateSyncJob(shopId, payload || {}),
    get: (shopId, jobId) => temuPlatformAPI.getCertificateSyncJob(shopId, jobId),
    cancel: (shopId, jobId) => temuPlatformAPI.cancelCertificateSyncJob(shopId, jobId),
  },
  certificateLibrary: {
    get label() { return fixedUi('证书库'); },
    mode: 'job',
    start: (shopId) => temuPlatformAPI.startCertificateLibrarySyncJob(shopId),
    get: (shopId, jobId) => temuPlatformAPI.getCertificateLibrarySyncJob(shopId, jobId),
    cancel: (shopId, jobId) => temuPlatformAPI.cancelCertificateLibrarySyncJob(shopId, jobId),
  },
  compliance: {
    get label() { return fixedUi('合规'); },
    mode: 'job',
    start: (shopId, payload) => temuPlatformAPI.startComplianceSyncJob(shopId, {
      scope: 'pull',
      ...(payload && typeof payload === 'object' ? payload : {}),
    }),
    get: (shopId, jobId) => temuPlatformAPI.getComplianceSyncJob(shopId, jobId),
    cancel: (shopId, jobId) => temuPlatformAPI.cancelComplianceSyncJob(shopId, jobId),
  },
  /**
   * @deprecated 旧「拉取审核状态」入口；cert.query 已归「产品证书资质」。
   * 保留别名，行为与 certificate 相同。
   */
  complianceAudit: {
    get label() { return fixedUi('产品证书资质'); },
    mode: 'job',
    start: (shopId, payload) => temuPlatformAPI.startCertificateSyncJob(shopId, payload || {}),
    get: (shopId, jobId) => temuPlatformAPI.getCertificateSyncJob(shopId, jobId),
    cancel: (shopId, jobId) => temuPlatformAPI.cancelCertificateSyncJob(shopId, jobId),
  },
  productTemplateBatchCopy: {
    get label() { return fixedUi('产品模板复制'); },
    mode: 'job',
    start: (shopId, payload) => temuPlatformAPI.startProductTemplateBatchCopyJob(shopId, payload || {}),
    get: (shopId, jobId) => temuPlatformAPI.getProductTemplateBatchCopyJob(shopId, jobId),
    cancel: (shopId, jobId) => temuPlatformAPI.cancelProductTemplateBatchCopyJob(shopId, jobId),
  },
  photoTemplateBatchCopy: {
    get label() { return fixedUi('实拍图模板复制'); },
    mode: 'job',
    start: (shopId, payload) => temuPlatformAPI.startPhotoTemplateBatchCopyJob(shopId, payload || {}),
    get: (shopId, jobId) => temuPlatformAPI.getPhotoTemplateBatchCopyJob(shopId, jobId),
    cancel: (shopId, jobId) => temuPlatformAPI.cancelPhotoTemplateBatchCopyJob(shopId, jobId),
  },
  certificateLibraryBatchCopy: {
    get label() { return fixedUi('证书库复制'); },
    mode: 'job',
    start: (shopId, payload) => temuPlatformAPI.startCertificateLibraryBatchCopyJob(shopId, payload || {}),
    get: (shopId, jobId) => temuPlatformAPI.getCertificateLibraryBatchCopyJob(shopId, jobId),
    cancel: (shopId, jobId) => temuPlatformAPI.cancelCertificateLibraryBatchCopyJob(shopId, jobId),
  },
  photoAutoBind: {
    get label() { return fixedUi('实拍图自动绑定'); },
    mode: 'job',
    start: (shopId) => temuPlatformAPI.startPhotoAutoBindJob(shopId),
    get: (shopId, jobId) => temuPlatformAPI.getPhotoAutoBindJob(shopId, jobId),
    cancel: (shopId, jobId) => temuPlatformAPI.cancelPhotoAutoBindJob(shopId, jobId),
  },
  certAutoBind: {
    get label() { return fixedUi('证书自动绑定'); },
    mode: 'job',
    start: (shopId, payload) => temuPlatformAPI.startCertAutoBindJob(shopId, payload || {}),
    get: (shopId, jobId) => temuPlatformAPI.getCertAutoBindJob(shopId, jobId),
    cancel: (shopId, jobId) => temuPlatformAPI.cancelCertAutoBindJob(shopId, jobId),
  },
  agencyAutoBind: {
    get label() { return fixedUi('代理自动绑定'); },
    mode: 'job',
    start: (shopId, payload) => temuPlatformAPI.startAgencyAutoBindJob(shopId, payload || {}),
    get: (shopId, jobId) => temuPlatformAPI.getAgencyAutoBindJob(shopId, jobId),
    cancel: (shopId, jobId) => temuPlatformAPI.cancelAgencyAutoBindJob(shopId, jobId),
  },
  finance: {
    get label() { return fixedUi('财务账单'); },
    mode: 'job',
    start: (shopId, payload) => temuPlatformAPI.startModuleSyncJob(shopId, { module: 'finance', ...(payload || {}) }),
    get: (shopId, jobId) => temuPlatformAPI.getModuleSyncJob(shopId, jobId, 'finance'),
    cancel: (shopId, jobId) => temuPlatformAPI.cancelModuleSyncJob(shopId, jobId, 'finance'),
  },
  returnOrders: {
    get label() { return fixedUi('退货单'); },
    mode: 'job',
    start: (shopId, payload) => temuPlatformAPI.startModuleSyncJob(shopId, { module: 'returnOrders', ...(payload || {}) }),
    get: (shopId, jobId) => temuPlatformAPI.getModuleSyncJob(shopId, jobId, 'returnOrders'),
    cancel: (shopId, jobId) => temuPlatformAPI.cancelModuleSyncJob(shopId, jobId, 'returnOrders'),
  },
  orders: {
    get label() { return fixedUi("订单"); },
    mode: 'job',
    start: (shopId, payload) => temuPlatformAPI.startModuleSyncJob(shopId, { module: 'orders', ...(payload || {}) }),
    get: (shopId, jobId) => temuPlatformAPI.getModuleSyncJob(shopId, jobId, 'orders'),
    cancel: (shopId, jobId) => temuPlatformAPI.cancelModuleSyncJob(shopId, jobId, 'orders'),
  },
  environmental: {
    get label() { return fixedUi('环保耗材'); },
    mode: 'job',
    start: (shopId, payload) => temuPlatformAPI.startModuleSyncJob(shopId, { module: 'environmental', ...(payload || {}) }),
    get: (shopId, jobId) => temuPlatformAPI.getModuleSyncJob(shopId, jobId, 'environmental'),
    cancel: (shopId, jobId) => temuPlatformAPI.cancelModuleSyncJob(shopId, jobId, 'environmental'),
  },
  shopProfile: {
    get label() { return fixedUi("店铺信息"); },
    mode: 'job',
    start: (shopId, payload) => temuPlatformAPI.startModuleSyncJob(shopId, { module: 'shopProfile', ...(payload || {}) }),
    get: (shopId, jobId) => temuPlatformAPI.getModuleSyncJob(shopId, jobId, 'shopProfile'),
    cancel: (shopId, jobId) => temuPlatformAPI.cancelModuleSyncJob(shopId, jobId, 'shopProfile'),
  },
  freightTemplates: {
    get label() { return fixedUi("运费模板"); },
    mode: 'job',
    start: (shopId, payload) => temuPlatformAPI.startModuleSyncJob(shopId, { module: 'freightTemplates', ...(payload || {}) }),
    get: (shopId, jobId) => temuPlatformAPI.getModuleSyncJob(shopId, jobId, 'freightTemplates'),
    cancel: (shopId, jobId) => temuPlatformAPI.cancelModuleSyncJob(shopId, jobId, 'freightTemplates'),
  },
  warehouses: {
    get label() { return fixedUi('发货仓'); },
    mode: 'job',
    start: (shopId, payload) => temuPlatformAPI.startModuleSyncJob(shopId, { module: 'warehouses', ...(payload || {}) }),
    get: (shopId, jobId) => temuPlatformAPI.getModuleSyncJob(shopId, jobId, 'warehouses'),
    cancel: (shopId, jobId) => temuPlatformAPI.cancelModuleSyncJob(shopId, jobId, 'warehouses'),
  },
  models: {
    get label() { return fixedUi("模特"); },
    mode: 'job',
    start: (shopId, payload) => temuPlatformAPI.startModuleSyncJob(shopId, { module: 'models', ...(payload || {}) }),
    get: (shopId, jobId) => temuPlatformAPI.getModuleSyncJob(shopId, jobId, 'models'),
    cancel: (shopId, jobId) => temuPlatformAPI.cancelModuleSyncJob(shopId, jobId, 'models'),
  },
  sizeCharts: {
    get label() { return fixedUi('尺码表'); },
    mode: 'job',
    start: (shopId, payload) => temuPlatformAPI.startModuleSyncJob(shopId, { module: 'sizeCharts', ...(payload || {}) }),
    get: (shopId, jobId) => temuPlatformAPI.getModuleSyncJob(shopId, jobId, 'sizeCharts'),
    cancel: (shopId, jobId) => temuPlatformAPI.cancelModuleSyncJob(shopId, jobId, 'sizeCharts'),
  },
  discuss: {
    get label() { return fixedUi('成本议价单'); },
    mode: 'job',
    start: (shopId, payload) => temuPlatformAPI.startModuleSyncJob(shopId, { module: 'discuss', ...(payload || {}) }),
    get: (shopId, jobId) => temuPlatformAPI.getModuleSyncJob(shopId, jobId, 'discuss'),
    cancel: (shopId, jobId) => temuPlatformAPI.cancelModuleSyncJob(shopId, jobId, 'discuss'),
  },
  rrpAudit: {
    get label() { return fixedUi('建议零售价审核'); },
    mode: 'job',
    start: (shopId, payload) => temuPlatformAPI.startModuleSyncJob(shopId, { module: 'rrpAudit', ...(payload || {}) }),
    get: (shopId, jobId) => temuPlatformAPI.getModuleSyncJob(shopId, jobId, 'rrpAudit'),
    cancel: (shopId, jobId) => temuPlatformAPI.cancelModuleSyncJob(shopId, jobId, 'rrpAudit'),
  },
  rrpSkc: {
    get label() { return fixedUi("建议零售价"); },
    mode: 'job',
    start: (shopId, payload) => temuPlatformAPI.startModuleSyncJob(shopId, { module: 'rrpSkc', ...(payload || {}) }),
    get: (shopId, jobId) => temuPlatformAPI.getModuleSyncJob(shopId, jobId, 'rrpSkc'),
    cancel: (shopId, jobId) => temuPlatformAPI.cancelModuleSyncJob(shopId, jobId, 'rrpSkc'),
  },
  skcAgency: {
    get label() { return fixedUi('合规代理绑定'); },
    mode: 'job',
    start: (shopId, payload) => temuPlatformAPI.startComplianceSyncJob(shopId, { scope: 'agency', ...(payload || {}) }),
    get: (shopId, jobId) => temuPlatformAPI.getComplianceSyncJob(shopId, jobId),
    cancel: (shopId, jobId) => temuPlatformAPI.cancelComplianceSyncJob(shopId, jobId),
  },
  agency: {
    get label() { return fixedUi('代理公司'); },
    mode: 'job',
    start: (shopId, payload) => temuPlatformAPI.startComplianceSyncJob(shopId, { scope: 'agency', ...(payload || {}) }),
    get: (shopId, jobId) => temuPlatformAPI.getComplianceSyncJob(shopId, jobId),
    cancel: (shopId, jobId) => temuPlatformAPI.cancelComplianceSyncJob(shopId, jobId),
  },
  /** 仓库库存仍同步阻塞；营销活动/报名记录已升为 module-sync job */
  stock: { get label() { return fixedUi('仓库库存'); }, mode: 'blocking' },
  marketing: {
    get label() { return fixedUi("活动列表"); },
    mode: 'job',
    start: (shopId, payload) => temuPlatformAPI.startModuleSyncJob(shopId, { module: 'marketing', ...(payload || {}) }),
    get: (shopId, jobId) => temuPlatformAPI.getModuleSyncJob(shopId, jobId, 'marketing'),
    cancel: (shopId, jobId) => temuPlatformAPI.cancelModuleSyncJob(shopId, jobId, 'marketing'),
  },
  marketingProduct: {
    get label() { return fixedUi('报名记录'); },
    mode: 'job',
    start: (shopId, payload) => temuPlatformAPI.startModuleSyncJob(shopId, { module: 'marketingProduct', ...(payload || {}) }),
    get: (shopId, jobId) => temuPlatformAPI.getModuleSyncJob(shopId, jobId, 'marketingProduct'),
    cancel: (shopId, jobId) => temuPlatformAPI.cancelModuleSyncJob(shopId, jobId, 'marketingProduct'),
  },
  custom: { get label() { return fixedUi('同步'); }, mode: 'blocking' },
};

function makeTrackId() {
  return `trk_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

function readStoredTracks() {
  if (typeof localStorage === 'undefined') return [];
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

/** 启动态直接从 localStorage 恢复，避免首帧空 tracks 写回把任务清掉 */
function loadInitialTracks() {
  return readStoredTracks()
    .filter((t) => {
      if (t.mode !== 'job') return false;
      const jid = String(t.jobId || '').trim();
      return jid && jid !== 'null' && jid !== 'undefined';
    })
    .filter((t) => t.status === 'queued' || t.status === 'running')
    .map((t) => ({
      ...t,
      mode: 'job',
      status: t.status === 'queued' || t.status === 'running' ? t.status : 'running',
      message: t.message || `${fixedUi('恢复')} ${fixedUi(t.label || '同步')} ${fixedUi('进度…')}`,
    }));
}

function toPersistableTrack(t) {
  return {
    id: t.id,
    kind: t.kind,
    mode: 'job',
    shopId: String(t.shopId),
    jobId: t.jobId,
    label: t.label,
    groupId: t.groupId || null,
    payload: t.payload || null,
    status: t.status,
    percent: t.percent || 0,
    peakPercent: t.peakPercent || 0,
    imported: t.imported || 0,
    message: t.message || '',
    startedAt: t.startedAt,
    silent: Boolean(t.silent),
  };
}

function writeStoredTracks(tracks) {
  if (typeof localStorage === 'undefined') return;
  try {
    // 仅持久化可恢复的 job 型任务
    const persistable = tracks
      .filter((t) => t.mode === 'job' && t.shopId)
      .filter((t) => {
        const jid = String(t.jobId || '').trim();
        return jid && jid !== 'null' && jid !== 'undefined';
      })
      .filter((t) => t.status === 'queued' || t.status === 'running')
      .map(toPersistableTrack);
    if (persistable.length === 0) {
      localStorage.removeItem(STORAGE_KEY);
    } else {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(persistable));
    }
  } catch {
    /* ignore */
  }
}

function isTerminalStatus(status) {
  const s = String(status || '').toLowerCase();
  return s === 'success' || s === 'failed' || s === 'cancelled' || s === 'done';
}

function normalizeStatus(status) {
  const s = String(status || '').toLowerCase();
  if (s === 'success' || s === 'failed' || s === 'cancelled' || s === 'queued' || s === 'running') {
    return s;
  }
  return 'running';
}

function toBarProgress(track) {
  if (!track) return null;
  const raw = track.raw && typeof track.raw === 'object' ? track.raw : {};
  return {
    visible: true,
    status: track.status,
    percent: track.peakPercent || track.percent || 0,
    imported: Number(track.imported ?? raw.imported) || 0,
    processed: Number(track.processed ?? raw.processed ?? raw.skcProcessed ?? raw.spuProcessed) || 0,
    total: Number(track.total ?? raw.total ?? raw.skcTotal ?? raw.spuTotal) || 0,
    skcProcessed: Number(raw.skcProcessed ?? track.skcProcessed) || 0,
    skcTotal: Number(raw.skcTotal ?? track.skcTotal) || 0,
    spuProcessed: Number(raw.spuProcessed ?? track.spuProcessed) || 0,
    spuTotal: Number(raw.spuTotal ?? track.spuTotal) || 0,
    message: track.message || `${fixedUi(track.label || '同步')}…`,
    queueAhead: Number(track.queueAhead ?? raw.queueAhead) || 0,
    queueTotal: Number(track.queueTotal ?? raw.queueTotal) || 0,
    queuePosition: Number(track.queuePosition ?? raw.queuePosition) || 0,
    runningCount: Number(track.runningCount ?? raw.runningCount) || 0,
    maxConcurrentShops: Number(track.maxConcurrentShops ?? raw.maxConcurrentShops) || 0,
    trackId: track.id,
    kind: track.kind,
    label: track.label,
    raw,
  };
}

export function TemuSyncJobProvider({ children }) {
  const { activeShop } = useTemuShop();
  const shopId = activeShop?.id != null ? String(activeShop.id) : '';

  const [tracks, setTracks] = useState(loadInitialTracks);
  const [cancellingIds, setCancellingIds] = useState(() => new Set());
  const [claimedKinds, setClaimedKinds] = useState(() => new Set());
  const tracksRef = useRef(tracks);
  const pollingRef = useRef(new Set());
  const abortControllersRef = useRef(new Map()); // trackId -> AbortController (blocking)
  const listenersRef = useRef(new Map()); // trackId -> Set<fn>
  const claimCountsRef = useRef(new Map()); // kind -> refCount
  const inflightSyncKeysRef = useRef(new Set());
  const persistReadyRef = useRef(false);
  const resumeStartedRef = useRef(false);

  useEffect(() => {
    tracksRef.current = tracks;
    // 首帧前勿写回：StrictMode/空初始态会把尚未恢复的 job 清掉
    if (!persistReadyRef.current) return;
    writeStoredTracks(tracks);
  }, [tracks]);

  const updateTrack = useCallback((id, patch) => {
    setTracks((prev) => prev.map((t) => {
      if (t.id !== id) return t;
      const next = { ...t, ...patch };
      const percent = Number(patch.percent != null ? patch.percent : t.percent) || 0;
      next.peakPercent = Math.max(Number(t.peakPercent) || 0, percent, Number(patch.peakPercent) || 0);
      // 展示用峰值，避免轮询偶发回退把进度条打回 1%
      next.percent = next.peakPercent;
      return next;
    }));
  }, []);

  const removeTrackLater = useCallback(async (id, delayMs = 1200) => {
    await sleep(delayMs);
    const gone = tracksRef.current.find((t) => t.id === id);
    if (gone) {
      inflightSyncKeysRef.current.delete(`${gone.shopId}:${gone.kind}`);
    }
    setTracks((prev) => prev.filter((t) => t.id !== id));
    listenersRef.current.delete(id);
  }, []);

  const emitDone = useCallback((track, result) => {
    const set = listenersRef.current.get(track.id);
    if (!set) return;
    set.forEach((fn) => {
      try {
        fn(result, track);
      } catch {
        /* ignore */
      }
    });
  }, []);

  const clearCancelling = useCallback((trackId) => {
    setCancellingIds((prev) => {
      if (!prev.has(trackId)) return prev;
      const next = new Set(prev);
      next.delete(trackId);
      return next;
    });
  }, []);

  const pollJobTrack = useCallback(async (track) => {
    if (!track?.id || !track.shopId) return;
    const initialJobId = String(track.jobId || '').trim();
    if (!initialJobId || initialJobId === 'null' || initialJobId === 'undefined') return;
    if (pollingRef.current.has(track.id)) return;
    const handler = TEMU_SYNC_KINDS[track.kind];
    if (!handler || handler.mode !== 'job' || typeof handler.get !== 'function') return;

    pollingRef.current.add(track.id);
    try {
      let latest = null;
      while (true) {
        const current = tracksRef.current.find((t) => t.id === track.id);
        if (!current) break;
        // cancelled 但尚未 done：正在收尾，继续轮询
        if (current.done === true) break;
        if (isTerminalStatus(current.status) && current.status !== 'cancelled') break;
        if (current.status === 'cancelled' && current.done === true) break;

        // 优先用入参 track.jobId：setTracks 异步，tracksRef 可能仍是 draft（jobId=null）
        const jobId = String(current.jobId || track.jobId || '').trim();
        if (!jobId || jobId === 'null' || jobId === 'undefined') {
          await sleep(200);
          continue;
        }

        latest = await handler.get(current.shopId, jobId);
        if (!latest) break;

        const status = normalizeStatus(latest.status);
        const jobDone = Boolean(latest.done);
        // cancelled + !done → 仍在终止中，保持 running 展示以便按钮显示「终止中…」
        const displayStatus = (status === 'cancelled' && !jobDone)
          ? 'running'
          : (jobDone && status === 'running' ? 'success' : status);
        const finished = jobDone
          || status === 'success'
          || status === 'failed'
          || (status === 'cancelled' && jobDone);

        updateTrack(track.id, {
          status: displayStatus,
          done: jobDone,
          percent: Number(latest.percent) || 0,
          imported: Number(latest.imported) || 0,
          processed: Number(latest.processed ?? latest.skcProcessed ?? latest.spuProcessed) || 0,
          total: Number(latest.total ?? latest.skcTotal ?? latest.spuTotal) || 0,
          skcProcessed: Number(latest.skcProcessed) || 0,
          skcTotal: Number(latest.skcTotal) || 0,
          spuProcessed: Number(latest.spuProcessed) || 0,
          spuTotal: Number(latest.spuTotal) || 0,
          message: latest.message || current.message,
          queueAhead: Number(latest.queueAhead) || 0,
          queueTotal: Number(latest.queueTotal) || 0,
          queuePosition: Number(latest.queuePosition) || 0,
          runningCount: Number(latest.runningCount) || 0,
          maxConcurrentShops: Number(latest.maxConcurrentShops) || 0,
          jobId,
          raw: latest,
        });

        if (finished) {
          const finalStatus = status === 'running' ? 'success' : status;
          clearCancelling(track.id);
          const finishedTrack = {
            ...current,
            jobId,
            status: finalStatus,
            done: true,
            percent: Number(latest.percent) || 100,
            imported: Number(latest.imported) || 0,
            message: latest.message,
            raw: latest,
          };
          if (finalStatus === 'success') {
            const toastType = resolveSyncToastType(finalStatus, latest);
            const toastMessage = latest.error
              || latest.message
              || `${fixedUi(current.label || '同步')} ${fixedUi('完成')}`;
            if (!current.silent || !listenersRef.current.get(track.id)?.size) {
              showToast(toastMessage, toastType);
            }
            // 同步成功：把平台原始 JSON 打到控制台，便于核对平台真实返回
            logTemuSyncPlatformDumpForSyncKind(current.shopId, current.kind, {
              label: current.label,
              reason: '同步完成',
            });
            emitDone(finishedTrack, { ok: toastType === 'success', view: latest });
            await removeTrackLater(track.id, 1200);
          } else if (finalStatus === 'cancelled') {
            if (!current.silent || !listenersRef.current.get(track.id)?.size) {
              showToast(latest.message || fixedUi("终止成功"), 'info');
            }
            emitDone(finishedTrack, { ok: false, cancelled: true, view: latest });
            await removeTrackLater(track.id, 800);
          } else {
            if (!current.silent || !listenersRef.current.get(track.id)?.size) {
              showToast(latest.error || latest.message || `${fixedUi(current.label || '同步')} ${fixedUi('失败')}`, 'error');
            }
            // 同步失败：同样把平台原始 JSON 打出来，便于定位是哪个接口返回了 errorCode
            logTemuSyncPlatformDumpForSyncKind(current.shopId, current.kind, {
              label: current.label,
              reason: '同步失败',
            });
            emitDone(finishedTrack, { ok: false, view: latest });
            await removeTrackLater(track.id, 2000);
          }
          break;
        }
        await sleep(600);
      }
    } catch (e) {
      clearCancelling(track.id);
      updateTrack(track.id, {
        status: 'failed',
        done: true,
        message: e?.message || fixedUi("同步失败"),
      });
      const current = tracksRef.current.find((t) => t.id === track.id) || track;
      if (!current.silent || !listenersRef.current.get(track.id)?.size) {
        showToast(e?.message || fixedUi("同步失败"), 'error');
      }
      emitDone(current, { ok: false, error: e });
      await removeTrackLater(track.id, 2000);
    } finally {
      pollingRef.current.delete(track.id);
    }
  }, [clearCancelling, emitDone, removeTrackLater, updateTrack]);

  // 允许持久化，并续上已恢复任务的轮询
  useEffect(() => {
    persistReadyRef.current = true;
    writeStoredTracks(tracksRef.current);
    if (resumeStartedRef.current) return;
    resumeStartedRef.current = true;
    tracksRef.current.forEach((t) => {
      if (t.mode === 'job' && t.jobId) {
        setTimeout(() => pollJobTrack(t), 0);
      }
    });
  }, [pollJobTrack]);

  // 挂载 window.ipeTemuJson：控制台里可随时拉取任意 kind 的平台原始 JSON
  useEffect(() => {
    installTemuPlatformJsonConsole({ getShopId: () => shopId });
  }, [shopId]);

  const claimSyncKinds = useCallback((kinds) => {
    const list = (Array.isArray(kinds) ? kinds : [kinds]).filter(Boolean).map(String);
    if (list.length === 0) return;
    list.forEach((kind) => {
      claimCountsRef.current.set(kind, (claimCountsRef.current.get(kind) || 0) + 1);
    });
    setClaimedKinds(new Set(
      [...claimCountsRef.current.entries()].filter(([, n]) => n > 0).map(([k]) => k),
    ));
  }, []);

  const releaseSyncKinds = useCallback((kinds) => {
    const list = (Array.isArray(kinds) ? kinds : [kinds]).filter(Boolean).map(String);
    if (list.length === 0) return;
    list.forEach((kind) => {
      const next = (claimCountsRef.current.get(kind) || 0) - 1;
      if (next <= 0) claimCountsRef.current.delete(kind);
      else claimCountsRef.current.set(kind, next);
    });
    setClaimedKinds(new Set(
      [...claimCountsRef.current.entries()].filter(([, n]) => n > 0).map(([k]) => k),
    ));
  }, []);

  const onTrackDone = useCallback((trackId, fn) => {
    if (!trackId || typeof fn !== 'function') return () => {};
    if (!listenersRef.current.has(trackId)) {
      listenersRef.current.set(trackId, new Set());
    }
    listenersRef.current.get(trackId).add(fn);
    return () => {
      listenersRef.current.get(trackId)?.delete(fn);
    };
  }, []);

  const startJob = useCallback(async ({
    kind,
    shopId: sid,
    payload = null,
    label,
    groupId = null,
    silent = false,
  }) => {
    const targetShopId = String(sid || shopId || '');
    if (!targetShopId) {
      showToast(fixedUi("请先选择已授权的 TEMU 店铺"), 'warning');
      return null;
    }
    const handler = TEMU_SYNC_KINDS[kind];
    if (!handler || handler.mode !== 'job') {
      throw new Error(`不支持的同步类型: ${kind}`);
    }
    const busy = tracksRef.current.some(
      (t) => String(t.shopId) === targetShopId
        && t.kind === kind
        && !t.groupId
        && !groupId
        && (t.status === 'queued' || t.status === 'running'),
    );
    const syncKey = `${targetShopId}:${kind}`;
    if (busy || inflightSyncKeysRef.current.has(syncKey)) {
      showToast(SHOP_SYNC_BUSY_MESSAGE, 'warning');
      return tracksRef.current.find(
        (t) => String(t.shopId) === targetShopId && t.kind === kind && (t.status === 'queued' || t.status === 'running'),
      ) || null;
    }
    inflightSyncKeysRef.current.add(syncKey);

    const id = makeTrackId();
    const trackLabel = label || `${fixedUi(handler.label)} ${fixedUi('同步')}`;
    const draft = {
      id,
      kind,
      mode: 'job',
      shopId: targetShopId,
      jobId: null,
      label: trackLabel,
      groupId,
      silent: Boolean(silent || groupId),
      payload,
      status: 'queued',
      // 与后端 job 起步对齐，避免排队假进度 18% → running 2% 的回落感
      percent: 2,
      peakPercent: 2,
      imported: 0,
      message: `${fixedUi('正在启动')} ${fixedUi(trackLabel)}…`,
      startedAt: new Date().toISOString(),
    };
    tracksRef.current = [...tracksRef.current, draft];
    setTracks((prev) => (prev.some((t) => t.id === id) ? prev : [...prev, draft]));

    try {
      const started = await handler.start(targetShopId, payload);
      const jobId = String(
        started?.jobId
        || started?.data?.jobId
        || started?.id
        || '',
      ).trim();
      if (!jobId || jobId === 'null' || jobId === 'undefined') {
        throw new Error('未返回同步任务 ID');
      }
      const next = {
        ...draft,
        jobId,
        status: normalizeStatus(started.status || started?.data?.status) || 'queued',
        percent: Number(started.percent ?? started?.data?.percent) || 0,
        peakPercent: Number(started.percent ?? started?.data?.percent) || 0,
        imported: Number(started.imported ?? started?.data?.imported) || 0,
        message: started.message || started?.data?.message || fixedUi('排队中…'),
        queueAhead: Number(started.queueAhead ?? started?.data?.queueAhead) || 0,
        queueTotal: Number(started.queueTotal ?? started?.data?.queueTotal) || 0,
        queuePosition: Number(started.queuePosition ?? started?.data?.queuePosition) || 0,
        runningCount: Number(started.runningCount ?? started?.data?.runningCount) || 0,
        maxConcurrentShops: Number(started.maxConcurrentShops ?? started?.data?.maxConcurrentShops) || 0,
        raw: started,
      };
      // 同步更新 ref，避免 poll 读到仍为 null 的 draft
      tracksRef.current = tracksRef.current.map((t) => (t.id === id ? next : t));
      if (!tracksRef.current.some((t) => t.id === id)) {
        tracksRef.current = [...tracksRef.current, next];
      }
      setTracks((prev) => {
        const mapped = prev.map((t) => (t.id === id ? next : t));
        return mapped.some((t) => t.id === id) ? mapped : [...mapped, next];
      });
      pollJobTrack(next);
      return next;
    } catch (e) {
      inflightSyncKeysRef.current.delete(syncKey);
      updateTrack(id, {
        status: 'failed',
        message: e?.message || fixedUi("启动同步失败"),
      });
      const busy = e?.code === 'SYNC_IN_PROGRESS' || /正在执行|同步进行中|当前店铺正在同步中/.test(e?.message || '');
      if (!silent && !groupId) {
        showToast(busy ? SHOP_SYNC_BUSY_MESSAGE : (e?.message || fixedUi("启动同步失败")), busy ? 'warning' : 'error');
      }
      await removeTrackLater(id, busy ? 800 : 2000);
      if (busy) {
        const err = e?.code === 'SYNC_IN_PROGRESS' ? e : new Error(SHOP_SYNC_BUSY_MESSAGE);
        err.code = 'SYNC_IN_PROGRESS';
        throw err;
      }
      throw e;
    }
  }, [shopId, pollJobTrack, removeTrackLater, updateTrack]);

  /** 并行启动多个 job，合并展示为一条进度（用于待办双同步） */
  const startJobGroup = useCallback(async ({
    shopId: sid,
    label = fixedUi("同步更新"),
    jobs = [],
  }) => {
    const targetShopId = String(sid || shopId || '');
    if (!targetShopId) {
      showToast(fixedUi("请先选择已授权的 TEMU 店铺"), 'warning');
      return null;
    }
    const kinds = (Array.isArray(jobs) ? jobs : []).map((j) => j?.kind).filter(Boolean);
    const groupBusy = kinds.some((kind) => (
      inflightSyncKeysRef.current.has(`${targetShopId}:${kind}`)
      || tracksRef.current.some(
        (t) => String(t.shopId) === targetShopId
          && t.kind === kind
          && (t.status === 'queued' || t.status === 'running'),
      )
    ));
    if (groupBusy) {
      showToast(SHOP_SYNC_BUSY_MESSAGE, 'warning');
      return null;
    }
    const groupId = makeTrackId();
    const groupTrack = {
      id: groupId,
      kind: 'custom',
      mode: 'group',
      shopId: targetShopId,
      jobId: null,
      label,
      groupId,
      status: 'running',
      percent: 5,
      peakPercent: 5,
      imported: 0,
      message: `${fixedUi('正在启动')} ${fixedUi(label)}…`,
      startedAt: new Date().toISOString(),
      childIds: [],
    };
    setTracks((prev) => [...prev, groupTrack]);

    const childIds = [];
    try {
      for (const job of jobs) {
        const child = await startJob({
          kind: job.kind,
          shopId: targetShopId,
          payload: job.payload,
          label: job.label,
          groupId,
          silent: true,
        });
        if (child?.id) childIds.push(child.id);
      }
      setTracks((prev) => prev.map((t) => (
        t.id === groupId ? { ...t, childIds, message: `${fixedUi(label)}…` } : t
      )));

      const mergeTimer = setInterval(() => {
        const kids = tracksRef.current.filter((t) => childIds.includes(t.id));
        if (kids.length === 0) return;
        const avg = Math.round(
          kids.reduce((sum, t) => sum + (t.peakPercent || t.percent || 0), 0) / Math.max(kids.length, 1),
        );
        const imported = kids.reduce((sum, t) => sum + (Number(t.imported) || 0), 0);
        const msgs = kids.map((t) => t.message).filter(Boolean);
        updateTrack(groupId, {
          percent: Math.max(5, avg),
          imported,
          message: msgs.join(' · ') || `${fixedUi(label)}…`,
        });
      }, 400);

      const childResults = await Promise.all(childIds.map((childId) => new Promise((resolve) => {
        const unsub = onTrackDone(childId, (result) => {
          unsub();
          resolve(result || { ok: false });
        });
        const check = () => {
          const live = tracksRef.current.find((t) => t.id === childId);
          if (!live || isTerminalStatus(live.status)) {
            unsub();
            resolve({
              ok: live?.status === 'success',
              cancelled: live?.status === 'cancelled',
              view: live?.raw,
            });
          }
        };
        setTimeout(check, 500);
        setTimeout(check, 1500);
      })));

      clearInterval(mergeTimer);

      const failed = childResults.some((r) => !r?.ok && !r?.cancelled);
      const cancelled = childResults.length > 0 && childResults.every((r) => r?.cancelled);
      const finalStatus = failed ? 'failed' : (cancelled ? 'cancelled' : 'success');
      updateTrack(groupId, {
        status: finalStatus,
        percent: 100,
        message: finalStatus === 'success' ? `${fixedUi(label)} ${fixedUi('完成')}` : (failed ? `${fixedUi(label)} ${fixedUi('失败')}` : `${fixedUi('已终止')} ${fixedUi(label)}`),
      });
      if (finalStatus === 'success') {
        showToast(`${fixedUi(label)} ${fixedUi('完成')}`, 'success');
      } else if (finalStatus === 'cancelled') {
        showToast(`${fixedUi('已终止')} ${fixedUi(label)}`, 'info');
      } else {
        showToast(`${fixedUi(label)} ${fixedUi('失败')}`, 'error');
      }
      const finished = tracksRef.current.find((t) => t.id === groupId) || groupTrack;
      emitDone(finished, { ok: finalStatus === 'success', children: childResults });
      await removeTrackLater(groupId, finalStatus === 'success' ? 1200 : 2000);
      return { groupId, children: childResults };
    } catch (e) {
      updateTrack(groupId, { status: 'failed', message: e?.message || '同步失败' });
      showToast(e?.message || fixedUi("同步失败"), 'error');
      await removeTrackLater(groupId, 2000);
      throw e;
    }
  }, [shopId, startJob, updateTrack, emitDone, removeTrackLater, onTrackDone]);

  const runBlocking = useCallback(async ({
    kind = 'custom',
    shopId: sid,
    label,
    syncFn,
    buildSuccess,
  }) => {
    const targetShopId = String(sid || shopId || '');
    if (!targetShopId) {
      showToast(fixedUi("请先选择已授权的 TEMU 店铺"), 'warning');
      return null;
    }
    if (typeof syncFn !== 'function') {
      throw new Error('syncFn 必填');
    }
    const meta = TEMU_SYNC_KINDS[kind] || TEMU_SYNC_KINDS.custom;
    const trackLabel = label || `${fixedUi(meta.label)} ${fixedUi('同步')}`;
    const already = tracksRef.current.some(
      (t) => String(t.shopId) === targetShopId
        && t.kind === kind
        && (t.status === 'queued' || t.status === 'running'),
    );
    const syncKey = `${targetShopId}:${kind}`;
    if (already || inflightSyncKeysRef.current.has(syncKey)) {
      const err = new Error(SHOP_SYNC_BUSY_MESSAGE);
      err.code = 'SYNC_IN_PROGRESS';
      showToast(err.message, 'warning');
      throw err;
    }
    inflightSyncKeysRef.current.add(syncKey);
    const id = makeTrackId();
    const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
    if (controller) {
      abortControllersRef.current.set(id, controller);
    }
    const draft = {
      id,
      kind,
      mode: 'blocking',
      shopId: targetShopId,
      jobId: null,
      label: trackLabel,
      status: 'queued',
      percent: 0,
      peakPercent: 0,
      imported: 0,
      message: `${fixedUi('正在启动')} ${fixedUi(trackLabel)}…`,
      startedAt: new Date().toISOString(),
    };
    tracksRef.current = [...tracksRef.current, draft];
    setTracks((prev) => (prev.some((t) => t.id === id) ? prev : [...prev, draft]));

    let peak = 0;
    const startedMs = Date.now();
    const timer = setInterval(() => {
      const live = tracksRef.current.find((t) => t.id === id);
      if (live?.cancelRequested) {
        updateTrack(id, {
          status: 'running',
          percent: peak,
          message: '正在终止…',
        });
        return;
      }
      peak = Math.min(92, peak + 1.5 + Math.random() * 4);
      const elapsedSec = Math.max(1, Math.round((Date.now() - startedMs) / 1000));
      updateTrack(id, {
        status: 'running',
        percent: peak,
        message: elapsedSec > 20
          ? `${fixedUi(trackLabel)}… ${fixedUi('已等待')} ${elapsedSec}s${fixedUi('（大时间段可能较久，请勿重复点击）')}`
          : `${fixedUi(trackLabel)}…`,
      });
    }, 450);

    try {
      updateTrack(id, { status: 'running', percent: 8, message: `${fixedUi(trackLabel)}…` });
      const result = await syncFn({ signal: controller?.signal });
      clearInterval(timer);
      if (tracksRef.current.find((t) => t.id === id)?.cancelRequested) {
        clearCancelling(id);
        updateTrack(id, {
          status: 'cancelled',
          done: true,
          percent: Math.max(peak, 0),
          message: '终止成功',
        });
        emitDone(draft, { ok: false, cancelled: true, result });
        showToast(fixedUi("终止成功"), 'info');
        await removeTrackLater(id, 800);
        return result;
      }
      const success = typeof buildSuccess === 'function' ? buildSuccess(result) : {};
      updateTrack(id, {
        status: 'success',
        done: true,
        percent: 100,
        imported: Number(success.imported) || 0,
        message: success.message || `${fixedUi(trackLabel)} ${fixedUi('完成')}`,
      });
      const finished = { ...draft, status: 'success', raw: result };
      // 阻塞式同步（库存等）完成后同样打印平台原始 JSON
      logTemuSyncPlatformDumpForSyncKind(targetShopId, kind, {
        label: trackLabel,
        reason: '同步完成',
      });
      emitDone(finished, { ok: true, result });
      if (success.silent !== true) {
        const toastType = success.toastType === 'warning' || success.toastType === 'error'
          ? success.toastType
          : 'success';
        showToast(success.message || `${fixedUi(trackLabel)} ${fixedUi('完成')}`, toastType);
      }
      await removeTrackLater(id, 1200);
      return result;
    } catch (e) {
      clearInterval(timer);
      clearCancelling(id);
      if (isRequestAborted(e) || tracksRef.current.find((t) => t.id === id)?.cancelRequested) {
        updateTrack(id, {
          status: 'cancelled',
          done: true,
          percent: Math.max(peak, 0),
          message: '终止成功',
        });
        emitDone(draft, { ok: false, cancelled: true, error: e });
        showToast(fixedUi("终止成功"), 'info');
        await removeTrackLater(id, 800);
        return null;
      }
      if (e?.code === 'SYNC_IN_PROGRESS' || /正在执行|当前店铺正在同步中|同步进行中/.test(e?.message || '')) {
        updateTrack(id, {
          status: 'failed',
          done: true,
          percent: Math.max(peak, 0),
          message: SHOP_SYNC_BUSY_MESSAGE,
        });
        emitDone(draft, { ok: false, error: e, skipped: true });
        showToast(SHOP_SYNC_BUSY_MESSAGE, 'warning');
        await removeTrackLater(id, 800);
        const err = e?.code === 'SYNC_IN_PROGRESS' ? e : new Error(SHOP_SYNC_BUSY_MESSAGE);
        err.code = 'SYNC_IN_PROGRESS';
        throw err;
      }
      updateTrack(id, {
        status: 'failed',
        done: true,
        percent: Math.max(peak, 0),
        message: e?.message || `${fixedUi(trackLabel)} ${fixedUi('失败')}`,
      });
      emitDone(draft, { ok: false, error: e });
      showToast(e?.message || `${fixedUi(trackLabel)} ${fixedUi('失败')}`, 'error');
      await removeTrackLater(id, 2000);
      throw e;
    } finally {
      abortControllersRef.current.delete(id);
    }
  }, [shopId, updateTrack, emitDone, removeTrackLater, clearCancelling]);

  /**
   * 外部已启动的 job（如 PlatformProductManagePage）登记到全局，便于切页/刷新后继续展示。
   */
  const adoptJob = useCallback(({
    kind,
    shopId: sid,
    jobId,
    label,
    initial,
  }) => {
    const targetShopId = String(sid || shopId || '');
    if (!targetShopId || !jobId) return null;
    const existing = tracksRef.current.find(
      (t) => String(t.shopId) === targetShopId
        && t.kind === kind
        && t.jobId === jobId
        && (t.status === 'queued' || t.status === 'running'),
    );
    if (existing) {
      if (initial) {
        updateTrack(existing.id, {
          percent: Number(initial.percent) || existing.percent,
          imported: Number(initial.imported) || existing.imported,
          message: initial.message || existing.message,
          status: normalizeStatus(initial.status) || existing.status,
        });
      }
      return existing;
    }
    const id = makeTrackId();
    const handler = TEMU_SYNC_KINDS[kind];
    const track = {
      id,
      kind,
      mode: 'job',
      shopId: targetShopId,
      jobId,
      label: label || `${fixedUi(handler?.label || '')} ${fixedUi('同步')}`,
      status: normalizeStatus(initial?.status) || 'running',
      percent: Number(initial?.percent) || 0,
      peakPercent: Number(initial?.percent) || 0,
      imported: Number(initial?.imported) || 0,
      message: initial?.message || fixedUi("同步中…"),
      startedAt: new Date().toISOString(),
    };
    setTracks((prev) => [...prev, track]);
    pollJobTrack(track);
    return track;
  }, [shopId, pollJobTrack, updateTrack]);

  // 布局外页面（商品同步）启动 job 时即时接入全局进度
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const onAdopt = (event) => {
      const detail = event?.detail;
      if (!detail?.jobId || !detail?.shopId) return;
      adoptJob({
        kind: detail.kind || 'product',
        shopId: detail.shopId,
        jobId: detail.jobId,
        label: detail.label,
        initial: detail.initial,
      });
    };
    window.addEventListener('ipe-temu-sync-adopt', onAdopt);
    return () => window.removeEventListener('ipe-temu-sync-adopt', onAdopt);
  }, [adoptJob]);

  const cancelTrack = useCallback(async (trackId) => {
    const track = tracksRef.current.find((t) => t.id === trackId);
    if (!track) return;
    if (track.mode === 'group') {
      const kids = tracksRef.current.filter((t) => t.groupId === track.groupId && t.id !== track.id);
      await Promise.all(kids.map((k) => cancelTrack(k.id)));
      return;
    }
    if (track.status !== 'queued' && track.status !== 'running') return;
    if (track.cancelRequested) return;

    setCancellingIds((prev) => new Set(prev).add(trackId));
    updateTrack(trackId, {
      cancelRequested: true,
      message: '正在终止…',
    });

    if (track.mode === 'blocking') {
      const controller = abortControllersRef.current.get(trackId);
      try {
        controller?.abort();
      } catch {
        /* ignore */
      }
      // 实际收尾由 runBlocking 的 abort/返回路径完成，并 toast「终止成功」
      return;
    }

    if (track.mode !== 'job' || !track.jobId) {
      clearCancelling(trackId);
      return;
    }
    const handler = TEMU_SYNC_KINDS[track.kind];
    if (!handler?.cancel) {
      clearCancelling(trackId);
      return;
    }
    try {
      const view = await handler.cancel(track.shopId, track.jobId);
      updateTrack(trackId, {
        message: view?.message || fixedUi("正在终止…"),
        done: Boolean(view?.done),
        status: view?.done ? 'cancelled' : 'running',
        raw: view || track.raw,
      });
      // done=true（如仅排队未开跑）：立即结束；否则等 poll 收尾
      if (view?.done) {
        clearCancelling(trackId);
        showToast(view?.message || fixedUi("终止成功"), 'info');
        emitDone({ ...track, status: 'cancelled', done: true, raw: view }, { ok: false, cancelled: true, view });
        await removeTrackLater(trackId, 800);
      }
    } catch (e) {
      showToast(e?.message || '终止同步失败', 'error');
      clearCancelling(trackId);
      updateTrack(trackId, { cancelRequested: false });
    }
  }, [clearCancelling, emitDone, removeTrackLater, updateTrack]);

  const isBusy = useCallback((kind, sid = shopId) => {
    const target = String(sid || '');
    return tracks.some(
      (t) => String(t.shopId) === target
        && (t.status === 'queued' || t.status === 'running')
        && (
          !kind
          || t.kind === kind
          || (t.mode === 'group' && Array.isArray(t.childIds) && tracks.some(
            (c) => t.childIds.includes(c.id) && c.kind === kind && (c.status === 'queued' || c.status === 'running'),
          ))
        ),
    );
  }, [tracks, shopId]);

  const activeBars = useMemo(() => {
    const relevant = tracks.filter((t) => {
      if (!shopId) return true;
      return String(t.shopId) === String(shopId);
    });
    // 有 group 时只展示 group 条，隐藏其子任务条
    const childIds = new Set();
    relevant.forEach((t) => {
      if (t.mode === 'group' && Array.isArray(t.childIds)) {
        t.childIds.forEach((id) => childIds.add(id));
      }
    });
    return relevant
      .filter((t) => !childIds.has(t.id))
      .filter((t) => t.status === 'queued' || t.status === 'running'
        || t.status === 'success' || t.status === 'failed' || t.status === 'cancelled')
      .map(toBarProgress)
      .filter(Boolean);
  }, [tracks, shopId]);

  /** 页面槽位认领后，全局顶栏不再重复展示同 kind */
  const globalBars = useMemo(
    () => activeBars.filter((bar) => !claimedKinds.has(String(bar.kind || ''))),
    [activeBars, claimedKinds],
  );

  const getBarsForKinds = useCallback((kinds) => {
    const set = new Set((Array.isArray(kinds) ? kinds : [kinds]).filter(Boolean).map(String));
    if (set.size === 0) return activeBars;
    return activeBars.filter((bar) => set.has(String(bar.kind || '')));
  }, [activeBars]);

  const value = useMemo(() => ({
    tracks,
    activeBars,
    globalBars,
    getBarsForKinds,
    claimSyncKinds,
    releaseSyncKinds,
    cancellingIds,
    startJob,
    startJobGroup,
    runBlocking,
    adoptJob,
    cancelTrack,
    isBusy,
    onTrackDone,
  }), [
    tracks,
    activeBars,
    globalBars,
    getBarsForKinds,
    claimSyncKinds,
    releaseSyncKinds,
    cancellingIds,
    startJob,
    startJobGroup,
    runBlocking,
    adoptJob,
    cancelTrack,
    isBusy,
    onTrackDone,
  ]);

  return (
    <TemuSyncJobContext.Provider value={value}>
      {children}
    </TemuSyncJobContext.Provider>
  );
}

/**
 * 布局外页面（如 PlatformProductManagePage）启动 job 后写入 localStorage，
 * 刷新后由 Provider hydrate 恢复进度。
 */
export function rememberActiveTemuJob({
  kind = 'product',
  shopId,
  jobId,
  label,
  payload = null,
  initial = null,
}) {
  if (typeof localStorage === 'undefined' || !shopId || !jobId) return;
  try {
    const targetShopId = String(shopId);
    const existing = readStoredTracks();
    const prev = existing.find(
      (t) => String(t.shopId) === targetShopId && t.kind === kind && t.jobId === jobId,
    );
    const handler = TEMU_SYNC_KINDS[kind];
    const track = toPersistableTrack({
      id: prev?.id || makeTrackId(),
      kind,
      shopId: targetShopId,
      jobId,
      label: label || `${fixedUi(handler?.label || '')} ${fixedUi('同步')}`,
      groupId: null,
      payload,
      status: normalizeStatus(initial?.status) || 'running',
      percent: Number(initial?.percent) || 0,
      peakPercent: Number(initial?.percent) || 0,
      imported: Number(initial?.imported) || 0,
      message: initial?.message || fixedUi("同步中…"),
      startedAt: prev?.startedAt || new Date().toISOString(),
    });
    const next = [
      ...existing.filter(
        (t) => !(String(t.shopId) === targetShopId && t.kind === kind && !t.groupId),
      ),
      track,
    ];
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('ipe-temu-sync-adopt', {
        detail: {
          kind,
          shopId: targetShopId,
          jobId,
          label: track.label,
          initial: {
            status: track.status,
            percent: track.percent,
            imported: track.imported,
            message: track.message,
          },
        },
      }));
    }
  } catch {
    /* ignore */
  }
}

export function useTemuSyncJobs() {
  const ctx = useContext(TemuSyncJobContext);
  if (!ctx) {
    throw new Error('useTemuSyncJobs 须在 TemuSyncJobProvider 内使用');
  }
  return ctx;
}

/** 安全版：布局外返回 null API，避免崩溃 */
export function useTemuSyncJobsOptional() {
  return useContext(TemuSyncJobContext);
}
